/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description handle analytics and butter action after sending a message
 */

import { ButterAction } from '@zcode-ui/web-zumper-global-state'
import { trackGAEvent } from '@zcode/nr-utils'
import type { InitialMessageData, MessageData, SendMessageArgs } from '@zcode/state-type'
import type { O } from 'ts-toolbelt'
import { DEFAULT_SEND_SUCCESS_MSG } from '../../../constants/message'
import type { Dispatch } from '../../../types'
import * as Analytics from '../../../utils/analytics'
import { handleButter } from './handleButter'

type Config = O.Pick<
  SendMessageArgs,
  'analyticChannel' | 'dlcMessagedProperties' | 'entity' | 'isMulti' | 'multiListables'
>

export interface LightMessageData extends InitialMessageData {
  readonly building?: never
  readonly listing?: never
}

interface BaseBaseHandleSuccessAnalyticsAndButterProps {
  readonly config: Config
  readonly showTourSuccessButterBar: boolean | undefined
  readonly showMessageSuccessButter: boolean | undefined
  readonly willShowMultiMessageModal: boolean
}

interface BaseTourHandleSuccessAnalyticsAndButterProps
  extends BaseBaseHandleSuccessAnalyticsAndButterProps {
  readonly hasInstantTourAvailability: boolean
  readonly showTourSuccessButterBar: boolean
}

interface BaseNonTourHandleSuccessAnalyticsAndButterProps
  extends BaseBaseHandleSuccessAnalyticsAndButterProps {
  readonly hasInstantTourAvailability?: boolean | undefined
  readonly showTourSuccessButterBar: false | undefined
}

type BaseHandleSuccessAnalyticsAndButterProps =
  | BaseTourHandleSuccessAnalyticsAndButterProps
  | BaseNonTourHandleSuccessAnalyticsAndButterProps

type LightHandleSuccessAnalyticsAndButterProps = BaseHandleSuccessAnalyticsAndButterProps & {
  readonly config: Config & { readonly isMulti?: false }
  readonly messageData: LightMessageData
}
type NormalHandleSuccessAnalyticsAndButterProps = BaseHandleSuccessAnalyticsAndButterProps & {
  readonly config: Config & { readonly isMulti: boolean | undefined }
  readonly messageData: MessageData
}

type HandleSuccessAnalyticsAndButterProps =
  | LightHandleSuccessAnalyticsAndButterProps
  | NormalHandleSuccessAnalyticsAndButterProps

function isMulti(
  props: HandleSuccessAnalyticsAndButterProps
): props is NormalHandleSuccessAnalyticsAndButterProps {
  return props.config.isMulti === true
}

const handleSuccessAnalyticsAndButter =
  (props: HandleSuccessAnalyticsAndButterProps) => (dispatch: Dispatch) => {
    const {
      config,
      hasInstantTourAvailability,
      messageData,
      willShowMultiMessageModal,
      showMessageSuccessButter,
      showTourSuccessButterBar,
    } = props
    const { analyticChannel } = config
    const { building, completionEvtName, feedNames, listing } = messageData

    // tour message butter bar logic
    if (showTourSuccessButterBar) {
      if (hasInstantTourAvailability) {
        dispatch(
          handleButter({
            msg: 'Booking requested. Check your email for confirmation.',
            type: ButterAction.SUCCESS_COLOR,
            duration: ButterAction.BUTTER_DURATION_A11Y_COMPLIANT,
          })
        )
      } else {
        dispatch(
          handleButter({
            msg: 'Tour requested. Check your email for details.',
            type: ButterAction.SUCCESS_COLOR,
            duration: ButterAction.BUTTER_DURATION_A11Y_COMPLIANT,
          })
        )
      }
    }

    // Analytics & Butter specific to multi v single messaging
    if (isMulti(props)) {
      // multi-msg track: multi message partial-failure error handler
      Analytics.trackMessageCompletion({
        target: props.messageData.originalTarget,
        result: true,
        num: feedNames?.length,
        evt: completionEvtName,
      })
    } else {
      // single-msg track
      Analytics.trackMessageCompletion({
        target: messageData,
        result: true,
      })
      if (!willShowMultiMessageModal && (building || listing)) {
        // don't show a success butter bar if MuliMessage is enabled
        const url = building?.url ?? listing?.url
        dispatch(
          ButterAction.addNotifyButterBar({
            actionText: building ? building.headerText : listing.headerText,
            action: () => {
              window.open(url, '_blank')
            },
            duration: ButterAction.BUTTER_DURATION_A11Y_COMPLIANT,
            color: ButterAction.SUCCESS_COLOR,
            message: DEFAULT_SEND_SUCCESS_MSG,
            isMessageButter: true,
          })
        )
      } else if (showMessageSuccessButter) {
        dispatch(
          handleButter({
            msg: 'Message sent!',
            type: ButterAction.SUCCESS_COLOR,
            duration: ButterAction.BUTTER_DURATION_A11Y_COMPLIANT,
          })
        )
      }
    }

    trackGAEvent(analyticChannel, 'MessageSend', 'Success')
  }

export default handleSuccessAnalyticsAndButter
